/**
 * VersionSelectModal Component
 *
 * Allows admin to browse available upgrade versions with metadata
 * before proceeding to the upgrade confirmation step.
 *
 * @module features/tenants/components/modals/VersionSelectModal
 */

import React, { useState } from 'react';
import { AlertIcon, ArrowUpIcon, CheckCircleIcon } from '../../../../components/common/Icons';
import type { Tenant } from '../../types';
import type { VersionDetail } from '../../api/upgradeApi';

interface VersionSelectModalProps {
  isOpen: boolean;
  tenant: Tenant | null;
  currentVersion: string;
  versions: VersionDetail[];
  loading: boolean;
  onClose: () => void;
  onSelect: (version: string) => void;
}

const INITIAL_DISPLAY_COUNT = 5;

const VersionBadges: React.FC<{ version: VersionDetail }> = ({ version }) => (
  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
    {version.is_latest && (
      <span className="badge badge-success" style={{ fontSize: '11px', padding: '2px 8px' }}>
        Recommended
      </span>
    )}
    {version.is_lts && (
      <span className="badge badge-info" style={{ fontSize: '11px', padding: '2px 8px' }}>
        LTS
      </span>
    )}
    {version.is_security_patch && (
      <span className="badge badge-warning" style={{ fontSize: '11px', padding: '2px 8px' }}>
        Security Patch
      </span>
    )}
    {(version.breaking_changes?.length ?? 0) > 0 && (
      <span className="badge badge-error" style={{ fontSize: '11px', padding: '2px 8px' }}>
        Breaking Changes
      </span>
    )}
  </div>
);

export const VersionSelectModal: React.FC<VersionSelectModalProps> = ({
  isOpen,
  tenant,
  currentVersion,
  versions,
  loading,
  onClose,
  onSelect,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  if (!isOpen || !tenant) return null;

  // Sort: is_latest first, then descending by version
  const sorted = [...versions].sort((a, b) => {
    if (a.is_latest && !b.is_latest) return -1;
    if (!a.is_latest && b.is_latest) return 1;
    return b.version.localeCompare(a.version, undefined, { numeric: true });
  });

  const displayVersions = showAll ? sorted : sorted.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = sorted.length > INITIAL_DISPLAY_COUNT;

  const toggleExpand = (version: string) => {
    setExpandedVersion(prev => prev === version ? null : version);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <h2>Select Upgrade Version</h2>
          <button className="btn btn-ghost" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body" style={{ overflow: 'auto', flex: 1 }}>
          {/* Current version reference */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            padding: '12px 16px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '8px',
            border: '1px solid var(--border-secondary)',
          }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              {tenant.name} is currently on
            </span>
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{currentVersion}</span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>
              Loading available versions...
            </div>
          ) : versions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>
              No upgrade versions available.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {displayVersions.map((v) => (
                  <div
                    key={v.version}
                    style={{
                      padding: '16px',
                      borderRadius: '10px',
                      border: v.is_latest
                        ? '1px solid rgba(16, 185, 129, 0.4)'
                        : '1px solid var(--border-secondary)',
                      background: v.is_latest
                        ? 'rgba(16, 185, 129, 0.05)'
                        : 'rgba(255,255,255,0.02)',
                      transition: 'border-color 0.15s ease',
                    }}
                  >
                    {/* Header row: version + badges + select button */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      marginBottom: v.description || (v.breaking_changes?.length ?? 0) > 0 || (v.new_features?.length ?? 0) > 0 ? '10px' : '0',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 700, fontSize: '15px', whiteSpace: 'nowrap' }}>
                          v{v.version}
                        </span>
                        <VersionBadges version={v} />
                        {v.released_at && (
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                            {formatDate(v.released_at)}
                          </span>
                        )}
                      </div>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => onSelect(v.version)}
                        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        <ArrowUpIcon size={14} />
                        Select
                      </button>
                    </div>

                    {/* Description */}
                    {v.description && (
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 8px 0', lineHeight: 1.4 }}>
                        {v.description}
                      </p>
                    )}

                    {/* Expandable details toggle */}
                    {((v.breaking_changes?.length ?? 0) > 0 || (v.new_features?.length ?? 0) > 0 || (v.changelog?.length ?? 0) > 0) && (
                      <>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => toggleExpand(v.version)}
                          style={{ fontSize: '12px', padding: '2px 8px', color: 'var(--primary)' }}
                        >
                          {expandedVersion === v.version ? 'Hide details \u25B4' : 'Show details \u25BE'}
                        </button>

                        {expandedVersion === v.version && (
                          <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {/* Breaking changes */}
                            {(v.breaking_changes?.length ?? 0) > 0 && (
                              <div style={{
                                padding: '10px 12px',
                                borderRadius: '6px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                              }}>
                                <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--error)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <AlertIcon size={14} /> Breaking Changes
                                </div>
                                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6 }}>
                                  {v.breaking_changes!.map((bc, i) => <li key={i}>{bc}</li>)}
                                </ul>
                              </div>
                            )}

                            {/* New features */}
                            {(v.new_features?.length ?? 0) > 0 && (
                              <div style={{
                                padding: '10px 12px',
                                borderRadius: '6px',
                                background: 'rgba(16, 185, 129, 0.08)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                              }}>
                                <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--success)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <CheckCircleIcon size={14} /> New Features
                                </div>
                                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6 }}>
                                  {v.new_features!.map((nf, i) => <li key={i}>{nf}</li>)}
                                </ul>
                              </div>
                            )}

                            {/* Changelog */}
                            {(v.changelog?.length ?? 0) > 0 && (
                              <div style={{
                                padding: '10px 12px',
                                borderRadius: '6px',
                                background: 'rgba(255,255,255,0.03)',
                                border: '1px solid var(--border-secondary)',
                              }}>
                                <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                                  Changelog
                                </div>
                                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                                  {v.changelog!.map((cl, i) => <li key={i}>{cl}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Show all / Show less toggle */}
              {hasMore && (
                <button
                  className="btn btn-ghost"
                  onClick={() => setShowAll(prev => !prev)}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    fontSize: '13px',
                    color: 'var(--primary)',
                  }}
                >
                  {showAll
                    ? `Show fewer (${INITIAL_DISPLAY_COUNT} of ${sorted.length})`
                    : `Show all ${sorted.length} versions`}
                </button>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};
